import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { auditLogsKeys, platformUsersKeys } from "../../../../api/queryKeys";
import TextButton from "../../../../components/buttons/TextButton";
import Datepicker from "../../../../components/Datepicker";
import FilterMenu from "../../../../components/filters/FilterMenu";
import FilterMenuRadio from "../../../../components/filters/FilterMenuRadio";
import LoadingIcon from "../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import { useLocalTime } from "../../../../utils/timestamp";
import { getDisplayEmail } from "../../account/utils";
import AuditLogsExportModal from "./AuditLogsExportModal";
import { getColumns } from "./columns";
import { getRows } from "./rows";

const LIMIT_OPTIONS = [
  { name: "50", label: "50 records", displayName: "50 records" },
  { name: "100", label: "100 records", displayName: "100 records" },
  { name: "250", label: "250 records", displayName: "250 records" },
  { name: "500", label: "500 records", displayName: "500 records" },
  { name: "1000", label: "1000 records", displayName: "1000 records" },
];

export default function AuditLogsView() {
  const localTime = useLocalTime();
  const { getAuditLogs, getAllPlatformUsers, exportAuditLogs } = useAPI();
  const [appliedFilters, setAppliedFilters] = useState({
    userEmail: {},
    recordsLimit: { 100: true },
  });
  const [selectedUserEmails, setSelectedUserEmails] = useState([]);
  const [selectedLimit, setSelectedLimit] = useState(100);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const platformUsersQuery = useQuery({
    queryKey: platformUsersKeys.all,
    queryFn: getAllPlatformUsers,
    select: (response) => response.data,
  });

  const validDateString = useMemo(() => {
    return selectedDate && selectedDate.isValid()
      ? selectedDate.format("YYYY-MM-DD")
      : null;
  }, [selectedDate]);

  const auditLogsQuery = useQuery({
    queryKey: auditLogsKeys.events(
      selectedUserEmails,
      selectedLimit,
      validDateString
    ),
    queryFn: () =>
      getAuditLogs(selectedUserEmails, selectedLimit, validDateString),
    select: (response) => response.data,
    staleTime: 0,
    enabled: true,
  });

  const platformUsers = useMemo(
    () =>
      platformUsersQuery?.data?.filter((user) => user?.is_visible !== false) ||
      [],
    [platformUsersQuery.data]
  );

  const columns = useMemo(() => getColumns(localTime), [localTime]);
  const rows = auditLogsQuery.isSuccess ? getRows(auditLogsQuery.data) : [];

  const handleSearch = () => {
    const selected = Object.keys(appliedFilters.userEmail || {}).filter(
      (key) => appliedFilters.userEmail[key] === true && key !== "All"
    );
    setSelectedUserEmails(selected);
    const limitSelected = Object.keys(appliedFilters.recordsLimit || {}).find(
      (key) => appliedFilters.recordsLimit[key] === true
    );
    if (limitSelected) {
      setSelectedLimit(parseInt(limitSelected));
    }
  };

  const handleExportAuditLogs = async ({ userEmails, fromDate, toDate }) => {
    try {
      setIsExporting(true);
      await exportAuditLogs(userEmails, fromDate, toDate);
      Toast.success("Export started. You will receive an email when ready.");
      setIsExportModalOpen(false);
    } catch (err) {
      Toast.error("Error exporting audit logs. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ ml: "16px" }}>
      <Typography variant="subtitle1">Audit Logs</Typography>

      {platformUsersQuery.isSuccess && platformUsersQuery.data.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            maxWidth: "100%",
          }}
        >
          <FilterMenu
            filterName="User Email"
            fieldName="userEmail"
            options={[
              {
                name: "All",
                label: "All Users",
                displayName: "All Users",
              },
              ...platformUsers.map((user) => ({
                name: user.original_email,
                label: user.original_email,
                displayName: getDisplayEmail(user),
              })),
            ]}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            showSearch={true}
          />
          <Box minWidth="210px" maxWidth="250px">
            <FilterMenuRadio
              filterName="Number of Records to fetch"
              fieldName="recordsLimit"
              options={LIMIT_OPTIONS}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </Box>
          <Datepicker
            disableFuture={true}
            label="Select Date"
            value={selectedDate}
            onChange={setSelectedDate}
            minWidth="140px"
          />
          <TextButton onClick={handleSearch} size="small">
            Search
          </TextButton>
          <TextButton onClick={() => setIsExportModalOpen(true)} size="small">
            Export
          </TextButton>
        </Box>
      )}

      {platformUsersQuery.isSuccess && platformUsersQuery.data.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No users available
        </Typography>
      )}

      {platformUsersQuery.isLoading && (
        <Typography variant="body2" color="textSecondary">
          Loading users...
        </Typography>
      )}

      {auditLogsQuery.isLoading ? (
        <LoadingIcon />
      ) : auditLogsQuery.isSuccess && auditLogsQuery.data.length === 0 ? (
        <Box>
          <Typography variant="subtitle2">
            {validDateString
              ? "No audit logs found for the selected date"
              : "No audit logs found"}
          </Typography>
        </Box>
      ) : auditLogsQuery.isSuccess ? (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box width="99%">
            <StyledDataGrid
              columns={columns}
              rows={rows}
              getRowId={(row) => `${row.timestamp}-${row.user_email}`}
              pageSizeOptionsOverride={[50, 100, 200]}
            />
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ paddingTop: "20px" }}
          >
            This page is currently unavailable
          </Typography>
        </Box>
      )}

      <AuditLogsExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportAuditLogs}
        users={platformUsers.map((user) => user.original_email)}
        isExporting={isExporting}
      />
    </Box>
  );
}
